import React, {
  createContext,
  useState,
  useContext,
  ReactNode,
  useEffect,
} from "react";
import { callErrorToast } from "../../../utilities"; // Function to handle and display errors
import { CodCollectionsService } from "../../../services/shipment/codCollections.ts";

// Create an instance of the CodCollectionsService to handle API calls
const codCollectionsService = new CodCollectionsService();

/**
 * Props for the DeleteCodCollectionProvider component.
 */
interface DeleteCodCollectionProviderProps {
  children: ReactNode; // React children components that will be wrapped by the provider
  data?: string[]; // List of warehouse IDs to be deleted
  callback?: () => void; // Optional function to execute after deletion
}

/**
 * Context value that will be shared with consumers.
 */
interface DeleteCodCollectionContextValue {
  loader: boolean; // Indicates whether a deletion request is in progress
  deleteCodCollections: () => void; // Function to trigger the deletion process
  data: string[]; // List of warehouse IDs to be deleted
  callback?: () => void; // Optional function to execute after deletion
}

// Create the context with an initial value of undefined
const DeleteCodCollectionContext = createContext<
  DeleteCodCollectionContextValue | undefined
>(undefined);

/**
 * Provider component that manages the deletion process of codCollections.
 * It wraps the children components and provides context data for deletion handling.
 */
const DeleteCodCollectionProvider: React.FC<
  DeleteCodCollectionProviderProps
> = ({ children, data = [], callback }) => {
  // State to store errors that may occur during deletion
  const [error, setError] = useState<unknown>(null);

  // State to track whether a deletion operation is currently in progress
  const [loader, setLoader] = useState<boolean>(false);

  /**
   * Function to delete all codCollections in the `data` list.
   * Calls the CodCollectionsService to delete each warehouse and updates the loader state.
   */
  const deleteCodCollections = (): void => {
    setLoader(true); // Set loading state to true before making API calls
console
    // Create an array of promises by calling the delete method for each warehouse ID
    const promises = data.map((id) => codCollectionsService.delete(id));

    // Execute all delete requests concurrently
    Promise.all(promises)
      .then(() => {
        setLoader(false); // Set loading state to false once deletion is complete

        // If a callback function is provided, execute it
        if (callback) {
          callback();
        }
      })
      .catch((err) => {
        setLoader(false); // Ensure loading state is reset even if an error occurs
        setError(err); // Store the error for error handling
      });
  };

  /**
   * Effect to display an error toast when an error occurs.
   * Triggers when the `error` state changes.
   */
  useEffect(() => {
    if (error) {
      callErrorToast(error); // Display the error using a toast notification
    }
  }, [error]);

  // Provide the loader state and delete function to consumers
  return (
    <DeleteCodCollectionContext.Provider
      value={{ loader, deleteCodCollections, data, callback }}
    >
      {children}
    </DeleteCodCollectionContext.Provider>
  );
};

/**
 * Custom hook to access the DeleteWarehouseContext.
 * Ensures that the context is used only within a DeleteWarehouseProvider.
 */
const useDeleteCodCollectionContext = (): DeleteCodCollectionContextValue => {
  const context = useContext(DeleteCodCollectionContext);

  // Throw an error if the hook is used outside the provider
  if (!context) {
    throw new Error(
      "useDeleteCodCollectionContext must be used within a DeleteCodCollectionProvider"
    );
  }

  return context;
};

// Export the provider and custom hook for use in other components
export { DeleteCodCollectionProvider, useDeleteCodCollectionContext };
